import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { showNotification } from '@/shared/components/Notification';
import CryptoJS from 'crypto-js';

const AuthContext = createContext();

const SECRET_KEY = process.env.REACT_APP_SECRET_LINK_EXTERNAL;

export const AuthProvider = ({ children }) => {
  const history = useHistory();
  const rtl = { direction: 'ltr' };
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = () => {
      // Primeiro, verifica se há usuário Google logado
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
        setLoading(false);
        return;
      }

      // Se não há usuário Google, verifica se há token criptografado
      const auth = localStorage.getItem('auth');
      if (auth) {
        try {
          const authData = JSON.parse(auth);
          if (authData.token) {
            // Cria um usuário temporário baseado no token
            const tempUser = {
              name: 'Usuário Externo',
              email: 'externo@transben.com.br',
              token: authData.token,
              accessToken: authData.token,
              isExternalUser: true,
            };
            setUser(tempUser);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Erro ao processar auth do localStorage:', error);
        }
      }

      // Se não há nenhuma forma de autenticação
      setUser(null);
      setLoading(false);
    };

    initializeAuth();

    // Listener para sincronizar mudanças de autenticação entre abas
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'auth') {
        console.log('🔄 Sincronizando autenticação entre abas...');
        initializeAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const setUserAndSync = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      // Dispara evento para sincronizar outras abas
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'user',
        newValue: JSON.stringify(userData)
      }));
    } else {
      localStorage.removeItem('user');
      // Dispara evento para sincronizar outras abas
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'user',
        newValue: null
      }));
    }
  }, []);

  const loginWithGoogle = useCallback((response) => {
    console.log('Processing Google login response:', response);
    
    try {
      const email = response.profileObj.email;
      console.log('User email:', email);
      
      if (email.endsWith('@transben.com.br')) {
        const userData = {
          name: response.profileObj.name,
          email: email,
          token: response.tokenId,
          imageUrl: response.profileObj.imageUrl,
          googleId: response.profileObj.googleId,
          accessToken: response.accessToken,
          expiresAt: response.expiresAt,
          isExternalUser: false, // Usuário agora tem acesso completo
        };
        console.log('User data to be stored:', userData);
        
        // Limpa dados de usuário externo
        localStorage.removeItem('auth');
        localStorage.removeItem('processedToken');
        
        setUserAndSync(userData);
        
        // Redireciona para a página principal ou mantém na página atual se for personalizada
        const currentPath = window.location.pathname;
        if (currentPath.includes('/personalizado/')) {
          // Se estava em uma tela personalizada, redireciona para a versão normal
          const normalPath = currentPath.replace('/personalizado/', '/');
          history.replace(normalPath);
        } else {
          history.replace('/pages/programacao/disponibilidade');
        }
      } else {
        showNotification(
          rtl,
          'danger',
          'Acesso Negado',
          'O login com o Google é permitido apenas para contas @transben.com.br.',
        );
      }
    } catch (error) {
      console.error('Error processing Google login:', error);
      showNotification(
        rtl,
        'danger',
        'Erro de Processamento',
        'Erro ao processar o login do Google. Tente novamente.',
      );
    }
  }, [history, rtl, setUserAndSync]);

  const loginWithToken = useCallback((encryptedToken) => {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY);
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

      let token, userInfo = {};
      
      try {
        const parsed = JSON.parse(decryptedText);
        
        if (parsed.type === 'direct_access') {
          // Payload estruturado com informações completas
          token = parsed.token;
          userInfo = {
            name: parsed.name || 'Usuário Externo',
            email: parsed.email || 'externo@transben.com.br',
            expiresAt: parsed.expiresAt,
          };
        } else {
          // Payload simples ou formato antigo
          token = parsed?.token || decryptedText;
          userInfo = {
            name: parsed?.name || 'Usuário Externo',
            email: parsed?.email || 'externo@transben.com.br',
          };
        }
      } catch {
        // Payload é apenas uma string (formato antigo)
        token = decryptedText;
        userInfo = {
          name: 'Usuário Externo',
          email: 'externo@transben.com.br',
        };
      }

      const tempUser = {
        name: userInfo.name,
        email: userInfo.email,
        token: token,
        accessToken: token, // Garante que accessToken seja definido
        isExternalUser: true,
        expiresAt: userInfo.expiresAt,
      };

      localStorage.setItem('auth', JSON.stringify({ token }));
      setUserAndSync(tempUser);
      
      console.log('✅ Usuário autenticado via token:', userInfo.name);
      return true;
    } catch (error) {
      console.error('❌ Erro ao processar token criptografado:', error);
      return false;
    }
  }, [setUserAndSync]);

  const logout = useCallback(() => {
    setUserAndSync(null);
    localStorage.removeItem('auth');
    localStorage.removeItem('processedToken');
    history.replace('/');
  }, [history, setUserAndSync]);

  return (
    <AuthContext.Provider value={{
      user, loading, login: loginWithGoogle, loginWithToken, logout,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);